import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
  PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import './PainelAdministrativo.css';

/**
 * Painel Administrativo — o que o gestor olha: faturamento por unidade, convênios e pacientes.
 *
 * O dinheiro daqui vem das GUIAS do atendimento (utils/faturamentoGuias.js no backend), a mesma
 * régua do Relatório de Faturamento. Não é a tabela FATURA, que só guarda o período em aberto.
 *
 * Cores: paleta categórica validada para daltonismo (CVD ΔE ≥ 8 em todos os pares) — a mesma cor
 * identifica a unidade nas barras e na série temporal, porque cor segue a entidade, não a ordem.
 */

const CORES_UNIDADE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
const CORES_ORIGEM = { SUS: '#4a3aa7', Particular: '#e87ba4', 'Convênio': '#008300' };
const COR_SERIE = '#2a78d6';
const COR_RECEBIDO = '#1baf7a';
const CINZA_EIXO = '#8a93a2';

const hoje = () => new Date().toISOString().split('T')[0];
const diasAtras = (n) => new Date(Date.now() - n * 86400000).toISOString().split('T')[0];

const moeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const moedaCurta = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1000000) return `R$ ${(n / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}mi`;
  if (Math.abs(n) >= 1000) return `R$ ${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}mil`;
  return `R$ ${n.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};
const numero = (v) => Number(v || 0).toLocaleString('pt-BR');
const pct = (v, casas = 1) => `${Number(v || 0).toFixed(casas)}%`;
const dataCurta = (iso) => (iso ? iso.split('-').reverse().slice(0, 2).join('/') : '');

function Variacao({ valor, anterior, formato }) {
  if (valor === null || valor === undefined) return null;
  const sobe = valor >= 0;
  return (
    <span className="pa-kpi-nota">
      <span className={`pa-var ${sobe ? 'sobe' : 'desce'}`}>
        {sobe ? '▲' : '▼'} {pct(Math.abs(valor))}
      </span>
      <span>vs. {formato ? formato(anterior) : numero(anterior)} no período anterior</span>
    </span>
  );
}

function TooltipCustom({ active, payload, label, formatador = moeda, titulo }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="pa-tooltip">
      <strong>{titulo ? titulo(label, payload) : label}</strong>
      {payload.map((p, i) => (
        <div key={i}>
          <i style={{ background: p.color || p.fill }} />
          {p.name}: <b>{formatador(p.value)}</b>
        </div>
      ))}
    </div>
  );
}

function ModalDetalhe({ detalhe, onFechar }) {
  if (!detalhe) return null;
  const { titulo, dados, total, loading, erro } = detalhe;
  return (
    <div className="pa-overlay" onClick={onFechar}>
      <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pa-modal-header">
          <div>
            <h2>{titulo}</h2>
            {total !== undefined && (
              <small>{total} atendimento{total === 1 ? '' : 's'}{total === 500 ? ' (500 maiores)' : ''}</small>
            )}
          </div>
          <button className="pa-modal-fechar" onClick={onFechar} aria-label="Fechar">&times;</button>
        </div>
        <div className="pa-modal-body">
          {loading && <div className="pa-loading"><div className="pa-spinner" />Carregando atendimentos...</div>}
          {erro && <div className="pa-erro">{erro}</div>}
          {!loading && !erro && dados && dados.length === 0 && <div className="pa-vazio">Nenhum atendimento no período.</div>}
          {!loading && !erro && dados && dados.length > 0 && (
            <div className="pa-tabela-scroll">
              <table className="pa-tabela">
                <thead>
                  <tr>
                    <th>Unidade</th><th>Protocolo</th><th>Data</th><th>Paciente</th>
                    <th>Convênio</th><th className="num">Exames</th><th className="num">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.map((r, i) => (
                    <tr key={i}>
                      <td>{r.LOCAL}</td>
                      <td>{r.PROTOCOLO}</td>
                      <td>{r.DATA ? r.DATA.split('-').reverse().join('/') : '-'}</td>
                      <td>{r.PACIENTE}</td>
                      <td>{r.CONVENIO}</td>
                      <td className="num">{numero(r.EXAMES)}</td>
                      <td className="num">{moeda(r.VALOR)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PainelAdministrativo() {
  const [dataInicio, setDataInicio] = useState(diasAtras(29));
  const [dataFim, setDataFim] = useState(hoje());
  const [unidades, setUnidades] = useState([]);       // filtro: ids selecionados
  const [convenio, setConvenio] = useState('');
  const [opcoes, setOpcoes] = useState({ locais: [], convenios: [] });

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [detalhe, setDetalhe] = useState(null);

  const atalhos = useMemo(() => {
    const d = new Date();
    const mesAtual = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
    const inicioMesPassado = new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().split('T')[0];
    const fimMesPassado = new Date(d.getFullYear(), d.getMonth(), 0).toISOString().split('T')[0];
    return [
      { rotulo: '7 dias', inicio: diasAtras(6), fim: hoje() },
      { rotulo: '30 dias', inicio: diasAtras(29), fim: hoje() },
      { rotulo: 'Mês atual', inicio: mesAtual, fim: hoje() },
      { rotulo: 'Mês passado', inicio: inicioMesPassado, fim: fimMesPassado },
      { rotulo: '90 dias', inicio: diasAtras(89), fim: hoje() }
    ];
  }, []);

  const buscar = useCallback(async (filtros) => {
    setCarregando(true);
    setErro('');
    const p = new URLSearchParams({ dataInicio: filtros.inicio, dataFim: filtros.fim });
    if (filtros.unidades?.length) p.append('local', filtros.unidades.join(','));
    if (filtros.convenio) p.append('convenio', filtros.convenio);
    try {
      const [painel, ops] = await Promise.all([
        axios.get(`${API_URL}/dashboard/administrativo?${p.toString()}`),
        axios.get(`${API_URL}/dashboard/administrativo/filtros?dataInicio=${filtros.inicio}&dataFim=${filtros.fim}`)
      ]);
      setDados(painel.data);
      setOpcoes(ops.data);
    } catch (e) {
      console.error('Erro ao carregar o painel administrativo:', e);
      setErro(e.response?.data?.error || 'Não foi possível carregar o painel. Tente novamente.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    buscar({ inicio: diasAtras(29), fim: hoje(), unidades: [], convenio: '' });
  }, [buscar]);

  const aplicar = () => buscar({ inicio: dataInicio, fim: dataFim, unidades, convenio });

  const usarAtalho = (a) => {
    setDataInicio(a.inicio);
    setDataFim(a.fim);
    buscar({ inicio: a.inicio, fim: a.fim, unidades, convenio });
  };

  const alternarUnidade = (id) => {
    setUnidades((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));
  };

  const abrirDetalhe = async (tipo, valor, titulo) => {
    setDetalhe({ titulo, loading: true });
    const p = new URLSearchParams({ tipo, valor, dataInicio, dataFim });
    if (unidades.length) p.append('local', unidades.join(','));
    if (convenio) p.append('convenio', convenio);
    try {
      const r = await axios.get(`${API_URL}/dashboard/administrativo/drilldown?${p.toString()}`);
      // O título do front tem o NOME ("Pedra do Indaiá"); o do backend só traz o código.
      setDetalhe({ titulo: titulo || r.data.titulo, dados: r.data.dados, total: r.data.total, loading: false });
    } catch (e) {
      setDetalhe({ titulo, loading: false, erro: e.response?.data?.error || 'Erro ao carregar o detalhamento.' });
    }
  };

  // Cor fixa por unidade, estável entre os gráficos (cor segue a entidade, não a ordem).
  const corPorUnidade = useMemo(() => {
    const mapa = {};
    (dados?.unidades || []).forEach((u, i) => { mapa[u.local_id] = CORES_UNIDADE[i % CORES_UNIDADE.length]; });
    return mapa;
  }, [dados]);

  // O eixo do gráfico usa o nome do convênio: o código do LifeSys vem cortado em 12 caracteres
  // ("SUS ITAPECER", "CL LABORATIV") e fica ilegível como rótulo.
  const topConvenios = useMemo(() => (dados?.convenios || []).slice(0, 10).map((c) => ({
    ...c,
    rotulo: (c.convenio_nome || c.convenio).length > 22
      ? `${(c.convenio_nome || c.convenio).slice(0, 21)}…`
      : (c.convenio_nome || c.convenio)
  })), [dados]);

  // A evolução vem "longa" (uma linha por dia/unidade); a área empilhada precisa dela pivotada.
  const serieTemporal = useMemo(() => {
    if (!dados?.evolucao) return [];
    const porDia = {};
    for (const p of dados.evolucao) {
      porDia[p.data] = porDia[p.data] || { data: p.data };
      porDia[p.data][p.local_id] = Number(p.faturamento || 0);
    }
    return Object.values(porDia).sort((a, b) => a.data.localeCompare(b.data));
  }, [dados]);

  const k = dados?.kpis;

  return (
    <div className="pa-root">
      <div className="pa-header">
        <div>
          <h1>📊 Painel Administrativo</h1>
          <p>Faturamento por unidade, convênios e pacientes — a visão do gestor.</p>
        </div>
        <span className="pa-fonte" title="O valor vem da soma das guias de cada atendimento, a mesma base do Relatório de Faturamento.">
          Valores apurados pelas guias do atendimento
        </span>
      </div>

      <div className="pa-filtros">
        <div className="pa-filtros-linha">
          <div className="pa-campo">
            <label htmlFor="pa-ini">Início</label>
            <input id="pa-ini" type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          </div>
          <div className="pa-campo">
            <label htmlFor="pa-fim">Fim</label>
            <input id="pa-fim" type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
          <div className="pa-campo">
            <label htmlFor="pa-conv">Convênio</label>
            <select id="pa-conv" value={convenio} onChange={(e) => setConvenio(e.target.value)}>
              <option value="">Todos</option>
              {opcoes.convenios.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          </div>
          <div className="pa-campo" style={{ flex: '1 1 220px' }}>
            <label>Unidades</label>
            <div className="pa-chips">
              {opcoes.locais.map((l, i) => (
                <button
                  key={l.id}
                  type="button"
                  className={`pa-chip ${unidades.includes(l.id) ? 'ativo' : ''}`}
                  onClick={() => alternarUnidade(l.id)}
                  title={`${numero(l.atendimentos)} atendimentos no período`}
                >
                  <i className="pa-ponto" style={{ color: CORES_UNIDADE[i % CORES_UNIDADE.length] }} />
                  {l.nome}
                </button>
              ))}
            </div>
          </div>
          <div className="pa-campo">
            <label>&nbsp;</label>
            <button className="pa-btn" onClick={aplicar} disabled={carregando}>
              {carregando ? 'Carregando...' : 'Aplicar'}
            </button>
          </div>
        </div>
        <div className="pa-atalhos" style={{ marginTop: 10 }}>
          {atalhos.map((a) => (
            <button
              key={a.rotulo}
              type="button"
              className={`pa-atalho ${dataInicio === a.inicio && dataFim === a.fim ? 'ativo' : ''}`}
              onClick={() => usarAtalho(a)}
            >
              {a.rotulo}
            </button>
          ))}
        </div>
      </div>

      {erro && <div className="pa-erro">{erro}</div>}

      {carregando && <div className="pa-loading"><div className="pa-spinner" />Apurando o período...</div>}

      {!carregando && dados && (
        <>
          {/* ------------------------------------------------------------ KPIs */}
          <div className="pa-kpis">
            <div className="pa-kpi">
              <div className="pa-kpi-label">Faturamento</div>
              <div className="pa-kpi-valor">{moeda(k.faturamento)}</div>
              <Variacao valor={k.variacao.faturamento} anterior={k.anterior.faturamento} formato={moedaCurta} />
            </div>
            <div className="pa-kpi" style={{ borderLeftColor: COR_RECEBIDO }}>
              <div className="pa-kpi-label">Recebido em caixa</div>
              <div className="pa-kpi-valor">{moeda(k.recebido)}</div>
              <div className="pa-kpi-nota">
                {k.faturamento ? pct((k.recebido / k.faturamento) * 100) : '—'} do faturado · convênio paga depois
              </div>
            </div>
            <div className="pa-kpi">
              <div className="pa-kpi-label">Atendimentos</div>
              <div className="pa-kpi-valor">{numero(k.atendimentos)}</div>
              <Variacao valor={k.variacao.atendimentos} anterior={k.anterior.atendimentos} />
            </div>
            <div className="pa-kpi">
              <div className="pa-kpi-label">Pacientes</div>
              <div className="pa-kpi-valor">{numero(k.pacientes)}</div>
              <Variacao valor={k.variacao.pacientes} anterior={k.anterior.pacientes} />
            </div>
            <div className="pa-kpi">
              <div className="pa-kpi-label">Ticket médio</div>
              <div className="pa-kpi-valor">{moeda(k.ticketMedio)}</div>
              <div className="pa-kpi-nota">por atendimento</div>
            </div>
            <div className="pa-kpi">
              <div className="pa-kpi-label">Exames</div>
              <div className="pa-kpi-valor">{numero(k.exames)}</div>
              <div className="pa-kpi-nota">{Number(k.examesPorAtendimento || 0).toFixed(1)} por atendimento</div>
            </div>
          </div>

          {/* --------------------------------------------- 1. FATURAMENTO POR UNIDADE */}
          <section className="pa-secao">
            <div className="pa-secao-titulo">
              <h2>1. Faturamento por unidade</h2>
              <span>clique na unidade para ver os atendimentos</span>
            </div>

            <div className="pa-grid pa-grid-2">
              <div className="pa-card">
                <h3>Faturado × recebido <small>mesma escala, em R$</small></h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dados.unidades} margin={{ top: 16, right: 8, left: 0, bottom: 4 }} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                    <XAxis dataKey="local_nome" tick={{ fontSize: 11, fill: CINZA_EIXO }} axisLine={{ stroke: '#e2e5ea' }} tickLine={false} />
                    <YAxis tickFormatter={moedaCurta} tick={{ fontSize: 11, fill: CINZA_EIXO }} axisLine={false} tickLine={false} width={76} />
                    <Tooltip content={<TooltipCustom />} cursor={{ fill: '#f5f7fa' }} />
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                    <Bar dataKey="faturamento" name="Faturado" fill={COR_SERIE} radius={[4, 4, 0, 0]} maxBarSize={64}
                      onClick={(d) => abrirDetalhe('unidade', d.local_id, `Unidade ${d.local_nome}`)} cursor="pointer">
                      <LabelList dataKey="faturamento" position="top" formatter={moedaCurta} style={{ fontSize: 10, fill: '#5b6472' }} />
                    </Bar>
                    <Bar dataKey="recebido" name="Recebido" fill={COR_RECEBIDO} radius={[4, 4, 0, 0]} maxBarSize={64} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="pa-card">
                <h3>Participação de cada unidade</h3>
                <div className="pa-tabela-scroll">
                  <table className="pa-tabela">
                    <thead>
                      <tr>
                        <th>Unidade</th>
                        <th className="num">Faturado</th>
                        <th className="num">Atend.</th>
                        <th className="num">Ticket</th>
                        <th className="num">Part.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.unidades.map((u) => (
                        <tr key={u.local_id} className="clicavel"
                          onClick={() => abrirDetalhe('unidade', u.local_id, `Unidade ${u.local_nome}`)}>
                          <td>
                            {u.local_nome}
                            <div className="pa-barra-mini">
                              <div style={{ width: `${u.participacao}%`, background: corPorUnidade[u.local_id] }} />
                            </div>
                          </td>
                          <td className="num">{moeda(u.faturamento)}</td>
                          <td className="num">{numero(u.atendimentos)}</td>
                          <td className="num">{moeda(u.ticket_medio)}</td>
                          <td className="num">{pct(u.participacao)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td className="num">{moeda(k.faturamento)}</td>
                        <td className="num">{numero(k.atendimentos)}</td>
                        <td className="num">{moeda(k.ticketMedio)}</td>
                        <td className="num">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="pa-card pa-card-full">
                <h3>Evolução diária do faturamento <small>empilhado por unidade</small></h3>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={serieTemporal} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                    <XAxis dataKey="data" tickFormatter={dataCurta} tick={{ fontSize: 11, fill: CINZA_EIXO }}
                      axisLine={{ stroke: '#e2e5ea' }} tickLine={false} minTickGap={22} />
                    <YAxis tickFormatter={moedaCurta} tick={{ fontSize: 11, fill: CINZA_EIXO }} axisLine={false} tickLine={false} width={76} />
                    <Tooltip content={<TooltipCustom titulo={(l) => l.split('-').reverse().join('/')} />}
                      cursor={{ stroke: '#bcc4d0', strokeWidth: 1 }} />
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                    {dados.unidades.map((u) => (
                      <Area
                        key={u.local_id}
                        type="monotone"
                        dataKey={u.local_id}
                        name={u.local_nome}
                        stackId="fat"
                        stroke={corPorUnidade[u.local_id]}
                        strokeWidth={2}
                        fill={corPorUnidade[u.local_id]}
                        fillOpacity={0.22}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* ------------------------------------------------------- 2. CONVÊNIOS */}
          <section className="pa-secao">
            <div className="pa-secao-titulo">
              <h2>2. Convênios</h2>
              <span>{dados.convenios.length} com movimento no período</span>
            </div>

            <div className="pa-grid pa-grid-2">
              <div className="pa-card">
                <h3>Origem do atendimento <small>SUS, particular ou convênio</small></h3>
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie
                      data={dados.categorias}
                      dataKey="faturamento"
                      nameKey="categoria"
                      cx="50%" cy="50%"
                      innerRadius={52} outerRadius={82}
                      paddingAngle={2}
                      onClick={(d) => abrirDetalhe('categoria', d.categoria, `Origem: ${d.categoria}`)}
                      cursor="pointer"
                    >
                      {dados.categorias.map((c) => (
                        <Cell key={c.categoria} fill={CORES_ORIGEM[c.categoria] || COR_SERIE} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip content={<TooltipCustom />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pa-legenda">
                  {dados.categorias.map((c) => (
                    <span key={c.categoria}>
                      <i style={{ background: CORES_ORIGEM[c.categoria] || COR_SERIE }} />
                      {c.categoria}: <b>&nbsp;{moeda(c.faturamento)}</b>
                      &nbsp;({pct(k.faturamento ? (c.faturamento / k.faturamento) * 100 : 0, 0)})
                    </span>
                  ))}
                </div>
              </div>

              <div className="pa-card">
                <h3>Top 10 convênios <small>por faturamento</small></h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topConvenios} layout="vertical" margin={{ top: 4, right: 56, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" horizontal={false} />
                    <XAxis type="number" tickFormatter={moedaCurta} tick={{ fontSize: 11, fill: CINZA_EIXO }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="rotulo" width={142} tick={{ fontSize: 11, fill: CINZA_EIXO }} axisLine={false} tickLine={false} />
                    <Tooltip content={<TooltipCustom />} cursor={{ fill: '#f5f7fa' }} />
                    <Bar dataKey="faturamento" name="Faturado" fill={COR_SERIE} radius={[0, 4, 4, 0]} maxBarSize={18}
                      onClick={(d) => abrirDetalhe('convenio', d.convenio, `Convênio ${d.convenio_nome || d.convenio}`)} cursor="pointer">
                      <LabelList dataKey="faturamento" position="right" formatter={moedaCurta} style={{ fontSize: 10, fill: '#5b6472' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="pa-card pa-card-full">
                <h3>Todos os convênios do período <small>clique na linha para ver os atendimentos</small></h3>
                <div className="pa-tabela-scroll">
                  <table className="pa-tabela">
                    <thead>
                      <tr>
                        <th>Convênio</th>
                        <th>Origem</th>
                        <th className="num">Faturado</th>
                        <th className="num">Atend.</th>
                        <th className="num">Exames</th>
                        <th className="num">Pacientes</th>
                        <th className="num">Ticket</th>
                        <th className="num">Part.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.convenios.map((c) => (
                        <tr key={c.convenio} className="clicavel"
                          onClick={() => abrirDetalhe('convenio', c.convenio, `Convênio ${c.convenio_nome || c.convenio}`)}>
                          <td>
                            <strong>{c.convenio_nome || c.convenio}</strong>
                            {c.convenio_nome && c.convenio_nome !== c.convenio && (
                              <div style={{ fontSize: '.72rem', color: '#8a93a2' }}>{c.convenio}</div>
                            )}
                          </td>
                          <td>
                            <span className="pa-legenda" style={{ margin: 0 }}>
                              <span><i style={{ background: CORES_ORIGEM[c.categoria] || COR_SERIE }} />{c.categoria}</span>
                            </span>
                          </td>
                          <td className="num">{moeda(c.faturamento)}</td>
                          <td className="num">{numero(c.atendimentos)}</td>
                          <td className="num">{numero(c.exames)}</td>
                          <td className="num">{numero(c.pacientes)}</td>
                          <td className="num">{moeda(c.ticket_medio)}</td>
                          <td className="num">{pct(c.participacao)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* ------------------------------------------------------- 3. PACIENTES */}
          <section className="pa-secao">
            <div className="pa-secao-titulo">
              <h2>3. Pacientes</h2>
              <span>quem foi atendido no período</span>
            </div>

            <div className="pa-kpis pa-kpis-compacto">
              <div className="pa-kpi">
                <div className="pa-kpi-label">Pacientes atendidos</div>
                <div className="pa-kpi-valor">{numero(dados.pacientes.total)}</div>
                <div className="pa-kpi-nota">
                  {dados.pacientes.total ? (k.atendimentos / dados.pacientes.total).toFixed(2) : '0'} atendimentos por paciente
                </div>
              </div>
              <div className="pa-kpi" style={{ borderLeftColor: CORES_UNIDADE[1] }}>
                <div className="pa-kpi-label">Novos</div>
                <div className="pa-kpi-valor">{numero(dados.pacientes.novos)}</div>
                <div className="pa-kpi-nota">
                  {dados.pacientes.total ? pct((dados.pacientes.novos / dados.pacientes.total) * 100) : '0%'} — primeira vez no laboratório
                </div>
              </div>
              <div className="pa-kpi" style={{ borderLeftColor: COR_RECEBIDO }}>
                <div className="pa-kpi-label">Recorrentes</div>
                <div className="pa-kpi-valor">{numero(dados.pacientes.recorrentes)}</div>
                <div className="pa-kpi-nota">já haviam sido atendidos antes</div>
              </div>
            </div>

            <div className="pa-grid pa-grid-3">
              <div className="pa-card">
                <h3>Faixa etária</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={dados.pacientes.porFaixaEtaria} margin={{ top: 16, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                    <XAxis dataKey="nome" tick={{ fontSize: 11, fill: CINZA_EIXO }} axisLine={{ stroke: '#e2e5ea' }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: CINZA_EIXO }} axisLine={false} tickLine={false} width={40} />
                    <Tooltip content={<TooltipCustom formatador={numero} />} cursor={{ fill: '#f5f7fa' }} />
                    <Bar dataKey="total" name="Pacientes" fill={COR_SERIE} radius={[4, 4, 0, 0]} maxBarSize={40}>
                      <LabelList dataKey="total" position="top" formatter={numero} style={{ fontSize: 10, fill: '#5b6472' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="pa-card">
                <h3>Sexo</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={dados.pacientes.porSexo} dataKey="total" nameKey="nome"
                      cx="50%" cy="50%" innerRadius={48} outerRadius={76} paddingAngle={2}>
                      {dados.pacientes.porSexo.map((s, i) => (
                        <Cell key={s.nome} fill={[COR_SERIE, '#4a3aa7', '#8a93a2'][i % 3]} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip content={<TooltipCustom formatador={numero} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pa-legenda">
                  {dados.pacientes.porSexo.map((s, i) => (
                    <span key={s.nome}>
                      <i style={{ background: [COR_SERIE, '#4a3aa7', '#8a93a2'][i % 3] }} />
                      {s.nome}: <b>&nbsp;{numero(s.total)}</b>
                    </span>
                  ))}
                </div>
              </div>

              <div className="pa-card">
                <h3>Cidades <small>top 12</small></h3>
                <div className="pa-tabela-scroll" style={{ maxHeight: 260, overflowY: 'auto' }}>
                  <table className="pa-tabela">
                    <thead>
                      <tr><th>Cidade</th><th className="num">Pacientes</th><th className="num">%</th></tr>
                    </thead>
                    <tbody>
                      {dados.pacientes.porCidade.map((c, i) => (
                        <tr key={`${c.cidade}-${i}`}>
                          <td>{c.cidade}{c.uf ? `/${c.uf}` : ''}</td>
                          <td className="num">{numero(c.total)}</td>
                          <td className="num">
                            {dados.pacientes.total ? pct((c.total / dados.pacientes.total) * 100) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* ---------------------------------------------------- caixa (apoio) */}
          {dados.recebimentoPorForma?.length > 0 && (
            <section className="pa-secao">
              <div className="pa-secao-titulo">
                <h2>Recebimento em caixa</h2>
                <span>o que entrou no período, por forma de pagamento</span>
              </div>
              <div className="pa-card">
                <div className="pa-tabela-scroll">
                  <table className="pa-tabela">
                    <thead>
                      <tr><th>Forma</th><th className="num">Valor</th><th className="num">Pagamentos</th><th className="num">Part.</th></tr>
                    </thead>
                    <tbody>
                      {dados.recebimentoPorForma.map((f) => (
                        <tr key={f.forma}>
                          <td>{f.forma}</td>
                          <td className="num">{moeda(f.valor)}</td>
                          <td className="num">{numero(f.quantidade)}</td>
                          <td className="num">{k.recebido ? pct((f.valor / k.recebido) * 100) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td className="num">{moeda(k.recebido)}</td>
                        <td className="num">
                          {numero(dados.recebimentoPorForma.reduce((s, f) => s + Number(f.quantidade || 0), 0))}
                        </td>
                        <td className="num">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </section>
          )}

          <p style={{ fontSize: '.73rem', color: '#8a93a2', textAlign: 'right' }}>
            Período de {dataInicio.split('-').reverse().join('/')} a {dataFim.split('-').reverse().join('/')}
            {dados.periodo?.anterior && ` · comparado com ${dados.periodo.anterior.inicio.split('-').reverse().join('/')} a ${dados.periodo.anterior.fim.split('-').reverse().join('/')}`}
            {dados._meta?.ms ? ` · apurado em ${(dados._meta.ms / 1000).toFixed(1)}s` : ''}
          </p>
        </>
      )}

      <ModalDetalhe detalhe={detalhe} onFechar={() => setDetalhe(null)} />
    </div>
  );
}

export default PainelAdministrativo;
